import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function ChatPanel({ bookingId, otherName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();
  const sinceRef = useRef(null);

  const poll = async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/chat/messages/${bookingId}`, { params });
      if (data && data.length) {
        setMessages(m => [...m, ...data]);
        sinceRef.current = data[data.length - 1].created_at;
      }
    } catch (e) {
      // Polling is best-effort; a dropped tick self-heals on the next one.
      console.warn("Chat poll failed:", e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chat/messages/${bookingId}`);
        if (!cancelled) {
          setMessages(data);
          if (data.length) sinceRef.current = data[data.length - 1].created_at;
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Could not load chat history:", e);
          toast.error("Could not load messages. Retrying…");
        }
      }
    })();
    const id = setInterval(poll, 3500);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { booking_id: bookingId, text });
      setMessages(m => [...m, data]);
      sinceRef.current = data.created_at;
      setText("");
    } catch (err) {
      // Never swallow this: the message would silently disappear and the user
      // would believe it was delivered. Keep `text` so they can retry.
      console.error("Failed to send chat message:", err);
      toast.error(formatApiError(err.response?.data?.detail) || "Message not sent — check your connection and try again");
    }
    setSending(false);
  };

  return (
    <div className="border border-border bg-card flex flex-col h-[420px]" data-testid="chat-panel">
      <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Booking chat</div>
          <div className="text-sm font-medium" data-testid="chat-other-name">{otherName}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-8">Start the conversation — say hello 👋</div>
        ) : messages.map(m => (
          <div key={m.message_id} className={`flex ${m.sender_role === "trainer" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[78%] px-3 py-2 text-sm border ${m.sender_role === "trainer" ? "bg-card border-border" : "bg-primary/10 border-primary/40"}`} data-testid={`chat-msg-${m.message_id}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.sender_name}</div>
              <div className="mt-0.5">{m.text}</div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="border-t border-border p-2 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
               className="rounded-sm" data-testid="chat-input" />
        <Button type="submit" size="sm" className="rounded-sm" disabled={sending || !text.trim()} data-testid="chat-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
